import { v4 as uuid } from 'uuid'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import {
  AssignmentScope,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { isRepeatableAssignmentEntity } from './assignmentUsageCounts'

export const getScopeGroupEntities = (
  ability: RaidPlanEncounterAssignmentAbility,
  scope: Pick<AssignmentScope, 'castIndexes'>,
  assignmentGroups: number,
  groupIndex: number,
) => {
  const representativeCast = normalizeAssignmentCast(
    ability.casts[scope.castIndexes[0]],
    assignmentGroups,
  )
  return normalizeAssignmentBox(representativeCast.boxes[groupIndex]).entities
}

export const getScopeEntityIdentities = (
  ability: RaidPlanEncounterAssignmentAbility,
  scope: Pick<AssignmentScope, 'castIndexes'>,
  assignmentGroups: number,
) => {
  const identities = new Set<string>()
  const representativeCast = normalizeAssignmentCast(
    ability.casts[scope.castIndexes[0]],
    assignmentGroups,
  )
  representativeCast.boxes.forEach((box) => {
    normalizeAssignmentBox(box).entities.forEach((entity) => {
      identities.add(getAssignmentEntityIdentity(entity))
    })
  })
  return identities
}

export const getScopeGroupEntityIdentities = (
  ability: RaidPlanEncounterAssignmentAbility,
  scope: Pick<AssignmentScope, 'castIndexes'>,
  assignmentGroups: number,
  groupIndex: number,
) => {
  const identities = new Set<string>()
  getScopeGroupEntities(ability, scope, assignmentGroups, groupIndex).forEach(
    (entity) => {
      identities.add(getAssignmentEntityIdentity(entity))
    },
  )
  return identities
}

export const applyScopeGroupEntities = (
  ability: RaidPlanEncounterAssignmentAbility,
  scope: Pick<AssignmentScope, 'castIndexes'>,
  assignmentGroups: number,
  groupIndex: number,
  nextEntities: RaidPlanAssignmentEntity[],
) => {
  scope.castIndexes.forEach((castIndex) => {
    const cast = normalizeAssignmentCast(
      ability.casts[castIndex],
      assignmentGroups,
    )
    const box = normalizeAssignmentBox(cast.boxes[groupIndex])
    box.entities = nextEntities.map((entity) => ({
      ...entity,
      id: entity.id ?? uuid(),
    }))
    cast.boxes[groupIndex] = box
    ability.casts[castIndex] = cast
  })
}

export const canAddAssignmentEntity = (
  entity: RaidPlanAssignmentEntity,
  {
    groupIdentities,
    scopeIdentities,
  }: {
    groupIdentities: Set<string>
    scopeIdentities: Set<string>
  },
) => {
  if (isRepeatableAssignmentEntity(entity)) return true
  const identity = getAssignmentEntityIdentity(entity)
  if (entity.kind === 'member') return !groupIdentities.has(identity)
  return !scopeIdentities.has(identity)
}
